import React, { useEffect, useMemo, useState } from 'react'
import { Spin, Alert, Select, Button } from 'antd'
import { CarOutlined, CheckCircleOutlined, ReloadOutlined, EnvironmentOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import './ParkingAvailability.css'
import { elmecLogo, englishFlag, italyFlag } from '../../assets'
import { useDispatch, useSelector } from 'react-redux'
import { secureApi } from '../../Config/api'
import { GET } from '../../utils/apis'
import { allPaths, translations } from '../../utils/constants'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { setDefaultLanguage } from '../../Redux/actions/defaultLanguageAction'
import io from 'socket.io-client'

const { Option } = Select

const PARKING_NAME = 'Parcheggio 1 Catania'

const ParkingAvailability = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const language = useSelector((state) => state.defaultLanguageReducer?.language)
    const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [error, setError] = useState('')
    const [parkingData, setParkingData] = useState({
        parkingName: PARKING_NAME,
        totalSpots: 50,
        occupiedSpots: 0,
        freeSpots: 50,
        lastUpdated: new Date().toISOString()
    })

    const handleLanguageChange = (value) => {
        dispatch(setDefaultLanguage(value))
    }

    useEffect(() => {
        const socket = io()
        socket.on('parking_session_created', () => {
            fetchParkingAvailability(true)
        })
        return () => {
            socket.off('parking_session_created')
            socket.disconnect()
        }
    }, [])

    const fetchParkingAvailability = async (isRefresh = false) => {
        try {
            setError('')

            if (isRefresh) {
                setRefreshing(true)
            } else {
                setLoading(true)
            }

            const response = await secureApi.get(GET?.GET_PARKING_AVAILABILITY)
            // console.log(response, 'response')
            const result = response?.data

            if (!response?.success && !result) {
                throw new Error('Unable to fetch parking availability')
            }

            setParkingData({
                parkingName: result?.parkingName || PARKING_NAME,
                totalSpots: Number(result?.totalSpots ?? 50),
                occupiedSpots: Number(result?.occupiedSpots ?? 0),
                freeSpots: Number(result?.freeSpots ?? 50),
                lastUpdated: result?.lastUpdated || new Date().toISOString()
            })
        } catch (err) {
            console.error('Parking availability error:', err)
            setError('Impossibile aggiornare i dati del parcheggio.')
        } finally {
            setLoading(false)
            setRefreshing(false)
        }
    }

    useEffect(() => {
        fetchParkingAvailability()

        const interval = setInterval(() => {
            fetchParkingAvailability(true)
        }, 5000)

        return () => clearInterval(interval)
    }, [])

    const occupancyPercent = useMemo(() => {
        if (!parkingData.totalSpots) return 0
        return Math.min(
            100,
            Math.round((parkingData.occupiedSpots / parkingData.totalSpots) * 100)
        )
    }, [parkingData])

    const formatDateTime = date => {
        if (!date) return '-'

        return new Date(date).toLocaleString('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        })
    }

    return (
        <>
            <div className='header-main-div'>
                <div className='header-inner-div'>
                    <div className='web-menu-div'>
                        <div>
                            <div className='logo-container'>
                                <div className='logo-main-div' style={{ cursor: 'pointer' }} onClick={() => { history.push(allPaths?.HOME) }}>
                                    <img src={elmecLogo} alt='Default Logo' className='sidelogo' />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='header-inner2-div'>
                        <div className='header-icons'>
                            <div className="header-language-div">
                                <Select
                                    defaultValue={language}
                                    style={{
                                        width: 30,
                                        marginRight: '11px',
                                    }}
                                    onChange={handleLanguageChange}
                                    suffixIcon={null}
                                    dropdownMatchSelectWidth={false}
                                    dropdownStyle={{
                                        minWidth: 5,
                                        padding: 0
                                    }}
                                    className="flag-select"
                                >
                                    <Option value="it">
                                        <img
                                            src={italyFlag}
                                            alt="Italian"
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '50%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </Option>
                                    <Option value="en">
                                        <img
                                            src={englishFlag}
                                            alt="English"
                                            style={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '50%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </Option>
                                </Select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="parking-public-page">
                <main className="parking-public-main">
                    {loading ? (
                        <div className="parking-loading-card">
                            <Spin size="large" />
                            <p>{translations[language]?.loadingAvailability}</p>
                        </div>
                    ) : (
                        <section className="parking-content-wrapper">

                            {/* ------------------------------------------- */}

                            {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}

                            <div className="parking-page-heading">
                                <div className="parking-location">
                                    <EnvironmentOutlined />
                                    {translations[language]?.catania}
                                </div>

                                <h1>{parkingData.parkingName}</h1>
                                <p>{translations[language]?.realTimeAvailability}</p>
                            </div>

                            <div className="parking-main-card">
                                <div className="parking-main-icon">
                                    <CarOutlined />
                                </div>

                                <p className="parking-main-label">
                                    {translations[language]?.remainingFreeSpots}
                                </p>

                                <h2 className="parking-free-count">
                                    {parkingData.freeSpots}
                                </h2>

                                <p className="parking-main-subtitle">
                                    {translations[language]?.availableSpots}
                                </p>

                                <div className="parking-progress-area">
                                    <div className="parking-progress-info">
                                        <span>{translations[language]?.occupancy}</span>
                                        <strong>{occupancyPercent}%</strong>
                                    </div>

                                    <div className="parking-progress-track">
                                        <div
                                            className="parking-progress-fill"
                                            style={{ width: `${occupancyPercent}%` }}
                                        />
                                    </div>
                                </div>

                                <div className="parking-last-update">
                                    <ReloadOutlined spin={refreshing} />
                                    <span>
                                        {translations[language]?.lastUpdate}{' '}
                                        {formatDateTime(parkingData.lastUpdated)}
                                    </span>
                                </div>
                            </div>

                            <div className="parking-stats-grid">
                                <div className="parking-stat-card">
                                    <div className="parking-stat-icon total">
                                        <CheckCircleOutlined />
                                    </div>

                                    <div>
                                        <p>{translations[language]?.totalSpots}</p>
                                        <h3>{parkingData.totalSpots}</h3>
                                    </div>
                                </div>

                                <div className="parking-stat-card">
                                    <div className="parking-stat-icon occupied">
                                        <CarOutlined />
                                    </div>

                                    <div>
                                        <p>{translations[language]?.occupiedSpots}</p>
                                        <h3>{parkingData.occupiedSpots}</h3>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}
                </main>
            </div>
        </>
    )
}

export default ParkingAvailability